import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react'

export interface DashboardFilters {
  service: string
  selectedTags: Array<{ id: number; label: string }>
}

const STORAGE_KEY_PREFIX = 'dashboard_filters'

/**
 * Custom hook to persist dashboard filters in sessionStorage
 * @param userId - User ID to create unique storage key per user
 * @returns Object containing filter states and setters
 */
export const useDashboardFilters = (userId: number | null) => {
  // Generate user-specific storage key
  const storageKey = userId ? `${STORAGE_KEY_PREFIX}_${userId}` : STORAGE_KEY_PREFIX

  // Load initial state from sessionStorage
  const loadFiltersFromStorage = useCallback((): DashboardFilters => {
    try {
      const stored = sessionStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored) as DashboardFilters
        // Validate the structure
        if (
          typeof parsed.service === 'string' &&
          Array.isArray(parsed.selectedTags)
        ) {
          return parsed
        }
      }
    } catch (error) {
      console.warn('Failed to load dashboard filters from sessionStorage:', error)
    }
    // Return default empty filters
    return {
      service: '',
      selectedTags: [],
    }
  }, [storageKey])

  // Initialize state with stored values or defaults
  const [service, setServiceState] = useState<string>(() => 
    loadFiltersFromStorage().service
  )
  const [selectedTags, setSelectedTagsState] = useState<
    Array<{ id: number; label: string }>
  >(() => loadFiltersFromStorage().selectedTags)

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      const filters: DashboardFilters = {
        service,
        selectedTags,
      }
      sessionStorage.setItem(storageKey, JSON.stringify(filters))
    } catch (error) {
      console.warn('Failed to save dashboard filters to sessionStorage:', error)
    }
  }, [service, selectedTags, storageKey])

  // Wrapped setters to maintain the same API as useState, supporting both direct values and updater functions
  const setService: Dispatch<SetStateAction<string>> = useCallback((value) => {
    setServiceState(value)
  }, [])

  const setSelectedTags: Dispatch<SetStateAction<Array<{ id: number; label: string }>>> =
    useCallback((tags) => {
      setSelectedTagsState(tags)
    }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setServiceState('')
    setSelectedTagsState([])
    try {
      sessionStorage.removeItem(storageKey)
    } catch (error) {
      console.warn('Failed to clear dashboard filters from sessionStorage:', error)
    }
  }, [storageKey])

  return {
    service,
    setService,
    selectedTags,
    setSelectedTags,
    clearFilters,
  }
}